import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDestinationDetails, useDestinations, useSources } from '../../api/hooks';
import type { DestinationDetail } from '../../types';
import { darkTheme, FONT_MONO } from '../../theme/tokens';
import {
  buildConnectionRows,
  connectionHref,
  type ConnectionRow,
  type ConnectionsBackState,
  type Direction,
} from './connectionModel';
import { Notice, StatusBadge } from './connectionUi';

const t = darkTheme;

type Filter = 'all' | Direction;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'inbound', label: 'Inbound' },
  { id: 'outbound', label: 'Outbound' },
];

const DIRECTION_LABEL: Record<Direction, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

function parseFilter(raw: string | null): Filter {
  return raw === 'inbound' || raw === 'outbound' ? raw : 'all';
}

/**
 * The connector registry as one table (SC-3122) — one row per connection
 * INSTANCE, in both directions, sorted so every instance of one integration sits
 * together. Settings live behind the row, in the detail view.
 *
 * Each direction is gated on its own permission, since the two can diverge under
 * a per-org override even though both floor at ORG_ADMIN today:
 * - Outbound rows come from the UNGATED list, so every member sees them. Only
 *   `manage_destinations` fetches the detail (for the "Being removed" state) and
 *   makes a row open.
 * - Inbound rows exist only with `view_sources` — the route 403s below it, so it
 *   is never requested rather than requested and hidden.
 *
 * The direction filter lives in `?direction=` so returning from a detail view
 * lands on the same filter.
 */
export function ConnectionsTable({
  canManageDestinations,
  canViewSources,
}: {
  canManageDestinations: boolean;
  canViewSources: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const filter: Filter = canViewSources ? parseFilter(params.get('direction')) : 'outbound';

  const destinations = useDestinations();
  const destinationList = useMemo(() => destinations.data ?? [], [destinations.data]);
  const detailQueries = useDestinationDetails(
    destinationList.map((d) => d.id),
    canManageDestinations,
  );
  const sources = useSources(canViewSources);

  const details = new Map<string, DestinationDetail>();
  detailQueries.forEach((q) => {
    if (q.data) details.set(q.data.id, q.data);
  });
  const rows = buildConnectionRows(destinationList, details, sources.data ?? []);
  const visible = filter === 'all' ? rows : rows.filter((r) => r.direction === filter);
  const count = (f: Filter) => (f === 'all' ? rows.length : rows.filter((r) => r.direction === f).length);

  const outboundPending = destinations.isPending;
  const inboundPending = canViewSources && sources.isPending;
  const loading =
    (filter !== 'inbound' && outboundPending) || (filter !== 'outbound' && inboundPending);
  const outboundError = filter !== 'inbound' && destinations.isError;
  const inboundError = canViewSources && filter !== 'outbound' && sources.isError;

  const setFilter = (f: Filter) => {
    const next = new URLSearchParams(params);
    if (f === 'all') next.delete('direction');
    else next.set('direction', f);
    setParams(next, { replace: true });
  };

  return (
    <div>
      {canViewSources && (
        <div
          role="group"
          aria-label="Direction"
          style={{
            display: 'inline-flex',
            gap: 2,
            padding: 3,
            marginBottom: 14,
            borderRadius: 8,
            background: t.surfaceDepressed,
            border: `1px solid ${t.border}`,
          }}
        >
          {FILTERS.map((f) => {
            const active = f.id === filter;
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.id)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  background: active ? t.surfaceAlt : 'transparent',
                  color: active ? t.text : t.text3,
                }}
              >
                {f.label}
                {!loading && <span style={{ marginLeft: 6, color: t.text3 }}>{count(f.id)}</span>}
              </button>
            );
          })}
        </div>
      )}

      {(outboundError || inboundError) && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          {outboundError && <Notice tone="error">Couldn’t load outbound connections.</Notice>}
          {inboundError && <Notice tone="error">Couldn’t load inbound connections.</Notice>}
        </div>
      )}

      {loading ? (
        <Notice>Loading connections…</Notice>
      ) : visible.length === 0 ? (
        // Only claim "none" when the side(s) in view actually loaded — a failed
        // read is not an empty one, and its alert above already says so.
        !(outboundError || inboundError) && <Notice>{emptyCopy(filter)}</Notice>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: t.text3 }}>
              <Th>Connection</Th>
              <Th>Direction</Th>
              <Th>Role</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <Row
                key={`${row.direction}:${row.id}`}
                row={row}
                opensDetail={row.direction === 'inbound' || canManageDestinations}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function emptyCopy(filter: Filter): string {
  if (filter === 'inbound') return 'No inbound connections yet.';
  if (filter === 'outbound') return 'No outbound connections yet.';
  return 'No connections are set up for your organization yet.';
}

function Th({ children }: { children: string }) {
  return (
    <th
      scope="col"
      style={{ fontWeight: 500, padding: '8px 12px', borderBottom: `1px solid ${t.border}` }}
    >
      {children}
    </th>
  );
}

function Row({ row, opensDetail }: { row: ConnectionRow; opensDetail: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const href = connectionHref(row.direction, row.id);
  // So the detail view's back link lands on the same filter.
  const state: ConnectionsBackState = { from: location.pathname + location.search };
  const cell = { padding: '12px', borderBottom: `1px solid ${t.border}`, verticalAlign: 'middle' as const };
  return (
    <tr
      // Mouse convenience only — the name is a real link, so keyboard and
      // screen-reader users reach the same place without the row handler.
      onClick={opensDetail ? () => navigate(href, { state }) : undefined}
      style={{ cursor: opensDetail ? 'pointer' : 'default' }}
    >
      <td style={cell}>
        {opensDetail ? (
          <Link to={href} state={state} style={{ color: t.text, fontWeight: 500, textDecoration: 'none' }}>
            {row.name}
          </Link>
        ) : (
          <span style={{ color: t.text, fontWeight: 500 }}>{row.name}</span>
        )}
        <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: t.text3, marginTop: 2 }}>
          {row.connectionKey}
        </div>
      </td>
      <td style={{ ...cell, color: t.text2 }}>{DIRECTION_LABEL[row.direction]}</td>
      <td style={{ ...cell, color: t.text2 }} title={row.role.hint}>
        {row.role.label}
      </td>
      <td style={cell}>
        <StatusBadge tone={row.status} />
      </td>
    </tr>
  );
}
